import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowTrendUp,
  faArrowTrendDown,
  faDollarSign,
  faShoppingCart,
  faUsers,
  faChartBar,
  faChartPie as faPieChart,
  faCircleExclamation,
  faBullseye,
} from '@fortawesome/free-solid-svg-icons';

import type {
  MerchantData,
  Order,
  OrderItem,
  Category,
  Item,
  Dispute,
  OverviewMetrics,
  TrendsData,
  CategorySales,
  ItemSales,
  TrendingItem,
  SalesAnalytics,
  AnalyticsData,
  TimeRange,
} from '../../../redux/MerchantAnalytics';
import type { OverviewData } from '../../../redux/MerchantAnalyticsOverview';

export type {
  MerchantData,
  Order,
  OrderItem,
  Category,
  Item,
  Dispute,
  OverviewMetrics,
  TrendsData,
  CategorySales,
  ItemSales,
  TrendingItem,
  SalesAnalytics,
  AnalyticsData,
  TimeRange,
};

export interface Store {
  id: string;
  name?: string;
  [key: string]: any;
}

export interface Customization {
  id: string;
  [key: string]: any;
}

export interface DateRange {
  startDate: string;
  endDate: string;
}

export interface AnalyticsTile {
  id: string;
  title: string;
  description: string;
  icon: any;
  color: string;
  bgColor: string;
  value: number | string;
  change: number;
  progress?: number;
}

export interface ChartData {
  salesTrend: {
    labels: string[];
    datasets: any[];
  };
  categoryDistribution: {
    labels: string[];
    datasets: any[];
  };
  topItems: {
    labels: string[];
    datasets: any[];
  };
}

export interface AnalyticsTileProps {
  title: string;
  icon: IconDefinition;
  value: number | string;
  trend?: "up" | "down";
  trendValue?: number;
  color: string;
  onClick?: () => void;
  subtitle?: string;
  progress?: number;
  accentColor?: string;
  loading?: boolean;
}

export const COLOR_MAP: Record<string, string> = {
  'green.500': '#52c41a',
  'blue.500': '#1890ff',
  'purple.500': '#722ed1',
  'orange.500': '#fa8c16',
  'teal.500': '#13c2c2',
  'cyan.500': '#00d9ff',
  'indigo.500': '#2f54eb',
  'yellow.500': '#fadb14',
  'red.500': '#ff4d4f',
};

export interface TileConfig {
  id: string;
  title: string;
  description: string;
  icon: IconDefinition;
  color: string;
  bgColor: string;
}

export const OVERVIEW_TILES_CONFIG: TileConfig[] = [
  { 
    id: 'totalSales',
    title: 'Total Sales',
    description: 'Total revenue generated',
    icon: faDollarSign,
    color: 'green.500',
    bgColor: 'green.50',
  },
  {
    id: 'totalOrders',
    title: 'Total Orders',
    description: 'Total number of orders',
    icon: faShoppingCart,
    color: 'blue.500',
    bgColor: 'blue.50',
  },
  {
    id: 'averageOrderValue',
    title: 'Average Order Value',
    description: 'Average value per order',
    icon: faBullseye,
    color: 'purple.500',
    bgColor: 'purple.50',
  },
  {
    id: 'totalCustomers',
    title: 'Total Customers',
    description: 'Total number of customers',
    icon: faUsers,
    color: 'orange.500',
    bgColor: 'orange.50',
  },
  {
    id: 'salesAnalytics',
    title: 'Sales Analytics',
    description: 'Detailed sales performance analysis',
    icon: faChartBar,
    color: 'teal.500',
    bgColor: 'teal.50',
  },
  {
    id: 'categorySales',
    title: 'Category Sales',
    description: 'Sales by product category',
    icon: faPieChart,
    color: 'cyan.500',
    bgColor: 'cyan.50',
  },
  {
    id: 'itemSales',
    title: 'Item Sales',
    description: 'Individual item performance',
    icon: faShoppingCart,
    color: 'indigo.500',
    bgColor: 'indigo.50',
  },
  {
    id: 'trendingItems',
    title: 'Trending Items',
    description: 'Trending and growth analysis',
    icon: faArrowTrendUp,
    color: 'yellow.500',
    bgColor: 'yellow.50',
  },
  {
    id: 'disputes',
    title: 'Disputes',
    description: 'Order disputes and resolutions',
    icon: faCircleExclamation,
    color: 'red.500',
    bgColor: 'red.50',
  },
];

export const TIME_RANGE_OPTIONS = [
  { key: '7d', value: '7d', label: 'Last 7 Days' },
  { key: '30d', value: '30d', label: 'Last 30 Days' },
  { key: '90d', value: '90d', label: 'Last 90 Days' },
  { key: '1y', value: '1y', label: 'Last Year' },
];

export const DEFAULT_TAB_KEY = 'overview';

export const TAB_CONFIG = [
  {
    key: 'overview',
    label: 'Overview',
  },
  {
    key: 'salesTrends',
    label: 'Sales Trends',
  },
  {
    key: 'categories',
    label: 'Categories',
  },
  {
    key: 'topItems',
    label: 'Top Items',
  },
];

export const CHART_COLORS = [
  '#3182CE',
  '#38A169',
  '#ED8936',
  '#E53E3E',
  '#805AD5',
  '#DD6B20',
  '#319795',
  '#D69E2E',
];

export const NAVIGATION_ROUTES: Record<string, string> = {
  sales: '/analytics/sales',
  salesAnalytics: '/analytics/sales',
  categorySales: '/analytics/category-sales',
  itemSales: '/analytics/item-sales',
  customers: '/analytics/customers',
  disputes: '/analytics/disputes',
  profitMargins: '/analytics/profit-margins',
  deliveryCostAnalysis: '/analytics/delivery-cost-analysis',
  popularItems: '/analytics/popular-items',
  trendingItems: '/analytics/trending-items',
  itemPerformanceComparison: '/analytics/item-performance-comparison',
};

export const MESSAGES = {
  EXPORT_SUCCESS: 'Analytics data has been exported successfully',
  EXPORT_ERROR: 'Failed to export analytics data. Please try again.',
  REFRESH_INFO: 'Refreshing analytics data...',
  FETCH_ERROR: 'Failed to fetch merchant profile data',
  WELCOME_PREFIX: 'Welcome back,',
  LAST_UPDATED_PREFIX: 'Your analytics were last updated',
  LAST_UPDATED_SUFFIX: "recently. Here's your restaurant's performance overview.",
} as const;

export const getDateRange = (range: TimeRange): DateRange => {
  const now = new Date();
  const startDate = new Date();

  switch (range) {
    case '7d':
      startDate.setDate(now.getDate() - 7);
      break;
    case '30d':
      startDate.setDate(now.getDate() - 30);
      break;
    case '90d':
      startDate.setDate(now.getDate() - 90);
      break;
    case '1y':
      startDate.setFullYear(now.getFullYear() - 1);
      break;
    default:
      startDate.setDate(now.getDate() - 7);
  }

  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: now.toISOString().split('T')[0],
  };
};

export const formatTimeAgo = (date: Date): string => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  let interval = Math.floor(seconds / 31536000);

  if (interval >= 1) return `${interval} year${interval > 1 ? 's' : ''} ago`;
  interval = Math.floor(seconds / 2592000);
  if (interval >= 1) return `${interval} month${interval > 1 ? 's' : ''} ago`;
  interval = Math.floor(seconds / 86400);
  if (interval >= 1) return `${interval} day${interval > 1 ? 's' : ''} ago`;
  interval = Math.floor(seconds / 3600);
  if (interval >= 1) return `${interval} hour${interval > 1 ? 's' : ''} ago`;
  interval = Math.floor(seconds / 60);
  if (interval >= 1) return `${interval} minute${interval > 1 ? 's' : ''} ago`;
  return 'Just now';
};

export const TREND_CONFIG = {
  up: {
    icon: faArrowTrendUp,
    color: '#52c41a',
  },
  down: {
    icon: faArrowTrendDown,
    color: '#ff4d4f',
  },
} as const;

// Format currency values (dollar amounts)
export const formatCurrency = (val: number): string => {
  if (val >= 1000000) {
    return `$${(val / 1000000).toFixed(2)}M`;
  } else if (val >= 1000) {
    return `$${(val / 1000).toFixed(1)}K`;
  }
  return `$${val.toFixed(2)}`;
};

// Format count values (plain numbers)
export const formatCount = (val: number): string => {
  if (val >= 1000000) {
    return `${(val / 1000000).toFixed(2)}M`;
  } else if (val >= 1000) {
    return `${(val / 1000).toFixed(1)}K`;
  }
  return val.toLocaleString();
};

export const formatValue = (val: number | string): string => {
  if (typeof val === 'number') {
    return formatCurrency(val);
  }
  return val;
};

export interface ChartConfig {
  key: string;
  title: string;
  description: string;
  tileId: string;
  icon: IconDefinition;
}

export const CHART_CONFIGS: Record<string, ChartConfig> = {
  salesTrends: {
    key: 'salesTrends',
    title: 'Sales Trends',
    description: 'Weekly sales performance over time',
    tileId: 'sales',
    icon: faChartBar,
  },
  categories: {
    key: 'categories',
    title: 'Category Distribution',
    description: 'Sales breakdown by menu categories',
    tileId: 'categorySales',
    icon: faPieChart,
  },
  topItems: {
    key: 'topItems',
    title: 'Top Performing Items',
    description: 'Best selling menu items by revenue',
    tileId: 'itemSales',
    icon: faShoppingCart,
  },
};

export const getTileValue = (
  configId: string,
  analyticsData: AnalyticsData | null,
  overviewData: OverviewData | null = null
): { value: number | string; change: number; progress: number } => {
  if (overviewData) {
    const overviewMap: Record<string, () => { value: number | string; change: number; progress: number }> = {
      totalSales: () => ({
        value: formatCurrency(overviewData.totalSales?.value || 0),
        change: overviewData.totalSales?.changePercentage || 0,
        progress: overviewData.totalSales?.progress || 0,
      }),
      totalOrders: () => ({
        value: formatCount(overviewData.totalOrders?.value || 0),
        change: overviewData.totalOrders?.changePercentage || 0,
        progress: overviewData.totalOrders?.progress || 0,
      }),
      averageOrderValue: () => ({
        value: formatCurrency(overviewData.averageOrderValue?.value || 0),
        change: overviewData.averageOrderValue?.changePercentage || 0,
        progress: overviewData.averageOrderValue?.progress || 0,
      }),
      totalCustomers: () => ({
        value: formatCount(overviewData.totalCustomers?.value || 0),
        change: overviewData.totalCustomers?.changePercentage || 0,
        progress: overviewData.totalCustomers?.progress || 0,
      }),
      salesAnalytics: () => ({
        value: formatCurrency(overviewData.salesAnalytics?.value || 0),
        change: overviewData.salesAnalytics?.changePercentage || 0,
        progress: overviewData.salesAnalytics?.progress || 0,
      }),
      categorySales: () => ({
        value: formatCurrency(overviewData.categorySales?.value || 0),
        change: overviewData.categorySales?.changePercentage || 0,
        progress: overviewData.categorySales?.progress || 0,
      }),
      itemSales: () => ({
        value: formatCurrency(overviewData.itemSales?.value || 0),
        change: overviewData.itemSales?.changePercentage || 0,
        progress: overviewData.itemSales?.progress || 0,
      }),
      trendingItems: () => ({
        value: formatCurrency(overviewData.trendingItems?.value || 0),
        change: overviewData.trendingItems?.changePercentage || 0,
        progress: overviewData.trendingItems?.progress || 0,
      }),
      disputes: () => ({
        value: formatCurrency(overviewData.disputes?.value || 0),
        change: overviewData.disputes?.changePercentage || 0,
        progress: overviewData.disputes?.progress || 0,
      }),
    };

    const result = overviewMap[configId]?.();
    if (result) {
      return result;
    }
  }
  if (!analyticsData) {
    return { value: 0, change: 0, progress: 0 };
  }

  const valueMap: Record<string, () => { value: number | string; change: number; progress: number }> = {
    totalSales: () => ({
      value: formatCurrency(analyticsData.overview?.totalSales || 0),
      change: analyticsData.overview?.growthRate || 0,
      progress: 0,
    }),
    totalOrders: () => ({
      value: formatCount(analyticsData.overview?.totalOrders || 0),
      change: 0,
      progress: 0,
    }),
    averageOrderValue: () => ({
      value: formatCurrency(analyticsData.overview?.averageOrderValue || 0),
      change: 0,
      progress: 0,
    }),
    totalCustomers: () => ({
      value: formatCount(analyticsData.overview?.totalCustomers || 0),
      change: 0,
      progress: 0,
    }),
    salesAnalytics: () => ({
      value: formatCurrency(analyticsData.salesAnalytics?.totalRevenue || 0),
      change: 0,
      progress: 0,
    }),
    categorySales: () => ({
      value: formatCount(Array.isArray(analyticsData.categorySales) ? analyticsData.categorySales.length : 0),
      change: 0,
      progress: 0,
    }),
    itemSales: () => ({
      value: formatCount(Array.isArray(analyticsData.itemSales) ? analyticsData.itemSales.length : 0),
      change: 0,
      progress: 0,
    }),
    trendingItems: () => ({
      value: formatCount(Array.isArray(analyticsData.trendingItems) ? analyticsData.trendingItems.length : 0),
      change: 0,
      progress: 0,
    }),
    disputes: () => ({
      value: formatCount(Array.isArray(analyticsData.disputes) ? analyticsData.disputes.length : 0),
      change: 0,
      progress: 0,
    }),
  };

  return valueMap[configId]?.() || { value: 0, change: 0, progress: 0 };
};

export const downloadFile = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};